// src/components/driverBolPdf.js
//
// Builds the driver-facing Bill of Lading as a real PDF file.
//
// Why not window.print(): drivers open this page inside the mobile app's
// webview, and iOS WKWebView does not implement window.print() — pressing the
// button did nothing at all. Generating the file ourselves works everywhere and
// also lets us control exactly what ends up on the document.
//
// Deliberately minimal: no icons, no pickup/delivery instructions, no notes.
// A BOL is a handover record — who carried what, from where to where, and who
// signed for it.

import React from 'react';
import { Document, Page, Text, View, StyleSheet, pdf, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: { flexDirection: 'column', backgroundColor: '#fff', padding: 28, fontSize: 10 },
  header: { marginBottom: 14, borderBottomWidth: 2, borderBottomColor: '#000', paddingBottom: 8 },
  title: { fontSize: 18, fontWeight: 'bold', textAlign: 'center' },
  loadId: { fontSize: 11, textAlign: 'center', marginTop: 4 },
  company: { fontSize: 13, textAlign: 'center', marginBottom: 2, fontWeight: 'bold' },
  companyLine: { fontSize: 9, textAlign: 'center', color: '#444' },

  section: { marginBottom: 12 },
  sectionTitle: {
    fontSize: 11, fontWeight: 'bold', marginBottom: 5,
    borderBottomWidth: 1, borderBottomColor: '#999', paddingBottom: 2
  },

  row: { flexDirection: 'row', marginBottom: 3 },
  label: { width: 90, color: '#555' },
  value: { flex: 1 },

  twoCol: { flexDirection: 'row', gap: 18 },
  col: { flex: 1 },

  vehicleRow: {
    flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#ddd', paddingVertical: 3
  },
  vehCell: { flex: 1 },
  vehVin: { flex: 1.4 },
  vehHead: { fontWeight: 'bold', borderBottomWidth: 1, borderBottomColor: '#999' },

  sigBox: { flex: 1, borderWidth: 1, borderColor: '#999', padding: 6, minHeight: 78 },
  sigImage: { height: 42, objectFit: 'contain', marginBottom: 4 },
  sigName: { fontSize: 9 },
  sigEmpty: { fontSize: 9, color: '#888', marginTop: 14, textAlign: 'center' },

  confidential: {
    marginTop: 18, fontSize: 8, textAlign: 'center', color: '#333',
    borderTopWidth: 1, borderTopColor: '#999', paddingTop: 6
  },
  footer: { marginTop: 6, fontSize: 8, color: '#777', textAlign: 'center' }
});

const fmtDate = (value) => {
  if (!value) return '';
  const d = value.toDate ? value.toDate() : new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const Field = ({ label, children }) => (
  <View style={styles.row}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{children || '—'}</Text>
  </View>
);

/**
 * Fetch an image and inline it as a data URI.
 *
 * Letting react-pdf fetch the signature URL itself produced a blank signature
 * box in the driver app's webview while "Signed by: <name>" still printed —
 * a BOL that looks unsigned. Fetching it here means the bytes are in hand
 * before the document is built, and a failure is something we can detect and
 * state rather than a silent gap on a legal document.
 */
async function fetchAsDataUri(url) {
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.error('Signature fetch failed:', response.status, url);
      return null;
    }
    const bytes = new Uint8Array(await response.arrayBuffer());
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
    const contentType = response.headers.get('content-type') || 'image/png';
    return `data:${contentType};base64,${btoa(binary)}`;
  } catch (error) {
    console.error('Signature fetch threw:', error.message);
    return null;
  }
}

const SignatureBox = ({ heading, url, dataUri, meta }) => (
  <View style={styles.sigBox}>
    <Text style={{ fontSize: 9, fontWeight: 'bold', marginBottom: 4 }}>{heading}</Text>
    {dataUri ? (
      <>
        <Image src={dataUri} style={styles.sigImage} />
        <Text style={styles.sigName}>Signed by: {meta?.signerName || '—'}</Text>
      </>
    ) : url ? (
      // Signed, but the image could not be retrieved. Saying so is essential:
      // a blank box beside a signer's name reads as an unsigned BOL.
      <>
        <Text style={styles.sigEmpty}>Signature on file — image unavailable</Text>
        <Text style={styles.sigName}>Signed by: {meta?.signerName || '—'}</Text>
      </>
    ) : (
      <Text style={styles.sigEmpty}>Not signed</Text>
    )}
  </View>
);

export function BOLDocument({ load, driver, companyName, companyAddress, companyPhone, signatureImages = {} }) {
  const vehicles = Array.isArray(load?.vehicles) ? load.vehicles : [];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          {/* The carrier, when there is only one it could be. A tenant running
              several companies gets no name here rather than a guessed one:
              this is the document a customer signs. */}
          {companyName ? <Text style={styles.company}>{companyName}</Text> : null}
          {companyAddress ? <Text style={styles.companyLine}>{companyAddress}</Text> : null}
          {companyPhone ? <Text style={styles.companyLine}>{companyPhone}</Text> : null}
          <Text style={styles.title}>BILL OF LADING</Text>
          <Text style={styles.loadId}>Load ID: {load?.load_id || '—'}</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Driver</Text>
          <Field label="Name">{driver?.name}</Field>
          {load?.mileage ? <Field label="Miles">{String(load.mileage)}</Field> : null}
        </View>

        <View style={styles.twoCol}>
          <View style={[styles.section, styles.col]}>
            <Text style={styles.sectionTitle}>Pickup</Text>
            <Field label="Location">{load?.pickupLocationName}</Field>
            <Field label="Address">{load?.pickupLocation}</Field>
            <Field label="Scheduled">{fmtDate(load?.pickupDateTime)}</Field>
            <Field label="Actual">{fmtDate(load?.actualPU || load?.actualPickupTimestamp)}</Field>
          </View>

          <View style={[styles.section, styles.col]}>
            <Text style={styles.sectionTitle}>Delivery</Text>
            <Field label="Location">{load?.deliveryLocationName}</Field>
            <Field label="Address">{load?.deliveryLocation}</Field>
            <Field label="Scheduled">{fmtDate(load?.deliveryDateTime)}</Field>
            <Field label="Actual">{fmtDate(load?.actualDEL)}</Field>
          </View>
        </View>

        {vehicles.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Vehicles ({vehicles.length})</Text>
            <View style={[styles.vehicleRow, styles.vehHead]}>
              <Text style={styles.vehCell}>Year</Text>
              <Text style={styles.vehCell}>Make</Text>
              <Text style={styles.vehCell}>Model</Text>
              <Text style={styles.vehVin}>VIN</Text>
            </View>
            {vehicles.map((v, i) => (
              <View style={styles.vehicleRow} key={i}>
                <Text style={styles.vehCell}>{v?.year || '—'}</Text>
                <Text style={styles.vehCell}>{v?.make || '—'}</Text>
                <Text style={styles.vehCell}>{v?.model || '—'}</Text>
                <Text style={styles.vehVin}>{v?.vin || '—'}</Text>
              </View>
            ))}
          </View>
        )}

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Signatures</Text>
          <View style={styles.twoCol}>
            <SignatureBox
              heading="Pickup"
              url={load?.pickupSignatureUrl}
              dataUri={signatureImages.pickup}
              meta={load?.pickupSignatureMetadata}
            />
            <SignatureBox
              heading="Delivery"
              url={load?.deliverySignatureUrl}
              dataUri={signatureImages.delivery}
              meta={load?.deliverySignatureMetadata}
            />
          </View>
        </View>

        <Text style={styles.confidential}>
          This BOL contains confidential information. Unauthorized access is prohibited.
        </Text>

        <Text style={styles.footer}>
          Generated {new Date().toLocaleDateString('en-US')}
        </Text>
      </Page>
    </Document>
  );
}

/**
 * Build the PDF and hand it to the device.
 *
 * Uses the same delivery mechanism that already works for drivers in
 * DriverLoadViewPage: a blob URL on a temporary anchor. Returns the filename so
 * the caller can report success.
 */
export async function downloadDriverBol({ load, driver, companyName, companyAddress, companyPhone }) {
  // Both signatures are fetched before the document is built, in parallel.
  const [pickup, delivery] = await Promise.all([
    fetchAsDataUri(load?.pickupSignatureUrl),
    fetchAsDataUri(load?.deliverySignatureUrl),
  ]);

  const blob = await pdf(
    <BOLDocument
      load={load}
      driver={driver}
      companyName={companyName}
      companyAddress={companyAddress}
      companyPhone={companyPhone}
      signatureImages={{ pickup, delivery }}
    />
  ).toBlob();

  const fileName = `BOL_${load?.load_id || 'load'}.pdf`;
  const url = URL.createObjectURL(blob);

  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    // Give the browser a moment to start the download before revoking.
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  }

  return fileName;
}
